import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import AdminLayout from "./AdminLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Save, FileText, AlertCircle, CheckCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminGrammarBlueprint() {
  const { user } = useAuth();
  const [packages, setPackages] = useState([]);
  const [blueprints, setBlueprints] = useState({});
  const [saving, setSaving] = useState({});
  const [saved, setSaved] = useState({});
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/packages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const activePackages = response.data.filter(p => p.is_active);
      setPackages(activePackages);
      
      // Initialize blueprints from package data
      const initialBlueprints = {};
      activePackages.forEach(pkg => {
        initialBlueprints[pkg.id] = (pkg.grammar_blueprint || []).join("\n");
      });
      setBlueprints(initialBlueprints);
    } catch (err) {
      setError("Paketler yüklenirken hata oluştu");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleBlueprintChange = (packageId, value) => {
    setBlueprints(prev => ({
      ...prev,
      [packageId]: value
    }));
    // Clear saved status when editing
    setSaved(prev => ({ ...prev, [packageId]: false }));
  };

  const handleSave = async (packageId) => {
    try {
      setSaving(prev => ({ ...prev, [packageId]: true }));
      setError(null);
      
      // Split by newline, trim, and filter empty lines
      const blueprintArray = blueprints[packageId]
        .split("\n")
        .map(line => line.trim())
        .filter(line => line.length > 0);
      
      const token = localStorage.getItem("token");
      await axios.put(`${API}/admin/packages/${packageId}/grammar-blueprint`, {
        grammar_blueprint: blueprintArray
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSaved(prev => ({ ...prev, [packageId]: true }));
      
      // Clear saved indicator after 3 seconds
      setTimeout(() => {
        setSaved(prev => ({ ...prev, [packageId]: false }));
      }, 3000);
    } catch (err) {
      setError(`Blueprint kaydedilirken hata: ${err.response?.data?.detail || err.message}`);
    } finally {
      setSaving(prev => ({ ...prev, [packageId]: false }));
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Grammar Blueprint">
        <div className="flex items-center justify-center h-64">
          <div className="text-neutral-400">Yükleniyor...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Grammar Blueprint">
      <div className="space-y-6">
        {/* Info Banner */}
        <Card className="bg-blue-900/20 border-blue-800">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-400 mt-0.5" />
              <div>
                <p className="text-blue-300 font-medium">Grammar Blueprint Nedir?</p>
                <p className="text-blue-400/80 text-sm mt-1">
                  Her seviye/ürün için izin verilen gramer konularını tanımlayın. 
                  Bu liste sadece ders oluştururken doğrulama için kullanılır, AI'a gönderilmez.
                  Her satıra bir gramer konusu yazın.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {error && (
          <Card className="bg-red-900/20 border-red-800">
            <CardContent className="pt-6">
              <p className="text-red-400">{error}</p>
            </CardContent>
          </Card>
        )}

        {/* Package Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {packages.map(pkg => (
            <Card key={pkg.id} className="bg-neutral-900 border-neutral-800">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-red-600/20 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-red-500" />
                    </div>
                    <div>
                      <CardTitle className="text-white text-lg">{pkg.name}</CardTitle>
                      <CardDescription className="text-neutral-500">
                        Seviye: {pkg.level}
                      </CardDescription>
                    </div>
                  </div>
                  {saved[pkg.id] && (
                    <div className="flex items-center gap-1 text-green-500 text-sm">
                      <CheckCircle className="w-4 h-4" />
                      Kaydedildi
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm text-neutral-400 mb-2">
                    Grammar Blueprint (sadece doğrulama için)
                  </label>
                  <Textarea
                    value={blueprints[pkg.id] || ""}
                    onChange={(e) => handleBlueprintChange(pkg.id, e.target.value)}
                    placeholder={`Örnek:\nam/is/are (to be)\npersonal pronouns\npossessive adjectives\napostrophe 's\nthere is/are\npresent simple (basic)`}
                    className="bg-neutral-800 border-neutral-700 text-white min-h-[200px] font-mono text-sm"
                    data-testid={`blueprint-textarea-${pkg.id}`}
                  />
                  <p className="text-xs text-neutral-500 mt-2">
                    Her satıra bir gramer konusu yazın. Bu konular ders oluştururken doğrulama için kullanılacak.
                  </p>
                </div>
                <Button
                  onClick={() => handleSave(pkg.id)}
                  disabled={saving[pkg.id]}
                  className="w-full bg-red-600 hover:bg-red-700"
                  data-testid={`save-blueprint-${pkg.id}`}
                >
                  {saving[pkg.id] ? (
                    "Kaydediliyor..."
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Blueprint Kaydet
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {packages.length === 0 && (
          <Card className="bg-neutral-900 border-neutral-800">
            <CardContent className="pt-6 text-center">
              <p className="text-neutral-400">
                Henüz aktif paket bulunmuyor. Önce "Ürünler/Paketler" sayfasından paket oluşturun.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
}
